import React, { useMemo } from 'react';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';
import { UIActions } from '../../ui/uiSlice';
import EditVerificationModal from '../EditVerificationModal';
import VerifiedIcon from '../VerifiedIcon';
import { useShouldRenderVerifyMenuItems } from '../VerifyMenuItems';
import VerifyPostModal from '../VerifyPostModal';
import getVerificationStatus, {
  VerificationStatus,
} from '../getVerificationStatus';
import isVerifier from '../isVerifier';
import usePostVerifyNavigation from '../usePostVerifyNavigation';
import ControlBarButton from './ControlBarButton';

const POST_FRAGMENT = gql(`
    fragment ControlBarVerificationButton_post on Post {
        id
        ...getVerificationStatus_post
        ...useShouldRenderVerifyMenuItems_post
        ...isVerifier_post
    }
`);

const useVerificationHandler = (post: DocumentType<typeof POST_FRAGMENT>) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const dispatch = useAppDispatch();
  const shouldRenderVerifyMenuItems = useShouldRenderVerifyMenuItems();

  const isUserAllowedToVerify = isVerifier(post, currentUser);
  const status = getVerificationStatus(post);
  const { postVerifyNavigate } = usePostVerifyNavigation(post.id);

  const handler = useMemo(() => {
    if (!post.publishedAt || !post.verification) {
      return null;
    }
    if (status === VerificationStatus.Expired) {
      if (isUserAllowedToVerify) {
        return () => {
          postVerifyNavigate();
        };
      }
      if (shouldRenderVerifyMenuItems({ post, user: currentUser })) {
        return () =>
          dispatch(
            UIActions.openModal(EditVerificationModal, {
              post,
            }),
          );
      }
    }
    if (status === VerificationStatus.Requested && isUserAllowedToVerify) {
      return () => dispatch(UIActions.openModal(VerifyPostModal, { post }));
    }
    return null;
  }, [
    currentUser,
    dispatch,
    isUserAllowedToVerify,
    post,
    shouldRenderVerifyMenuItems,
    status,
    postVerifyNavigate,
  ]);

  return handler;
};

interface ControlBarVerificationButtonProps {
  post: DocumentType<typeof POST_FRAGMENT>;
}

const ControlBarVerificationButton = ({
  post,
}: ControlBarVerificationButtonProps) => {
  const status = getVerificationStatus(post);

  const handler = useVerificationHandler(post);

  if (!handler) {
    return null;
  }

  return (
    <ControlBarButton
      controlButtonVariant={
        status === VerificationStatus.Expired ? 'warning' : 'info'
      }
      onClick={handler}
      icon={
        <VerifiedIcon
          variant={status === VerificationStatus.Expired ? 'warning' : 'info'}
        />
      }
    >
      <div>
        {status === VerificationStatus.Expired
          ? 'Verify expired'
          : 'Verify requested'}
      </div>
    </ControlBarButton>
  );
};

export default ControlBarVerificationButton;
